import React from 'react';
import {Banner} from 'app/components/banner/Banner';
import {Notifications} from '@mui/icons-material';
import {updatePushEntity} from 'app/services/follow';
import {useDispatch, useSelector} from 'react-redux';
import {createSelector} from 'reselect';
import {askForEntityPushFinished, fetchFollows} from 'app/actions/currentUserActions';
import {updateSnackbarInfo} from 'app/components/snackbar/SnackbarActions';
import {selectCollapsedAppBar} from 'app/hooks/reduxCreateSelectorHooks';
import {pushStatus} from 'app/components/snackbar/SnackbarContent';
import {captureSentryException} from 'app/services/sentry/sentryLogging';
import {useNative} from 'app/contexts/NativeContext';

const selectFollowPush = createSelector(
  state => state.currentUser,
  currentUser => {
    return {
      fuPaUser: currentUser.fuPaUser,
      entity: currentUser.newFollow.entity,
      askForPush: currentUser.newFollow.askForPush,
      pushSupport: currentUser.pushSupport,
      messagingDeviceToken: currentUser.messagingDeviceToken,
      notificationPermission: currentUser.notificationPermission,
    };
  }
);

const NotificationBanner = () => {
  const {entity, askForPush, pushSupport, messagingDeviceToken, notificationPermission} = useSelector(selectFollowPush);
  const collapsedAppBar = useSelector(selectCollapsedAppBar);
  const dispatch = useDispatch();
  const icon = <Notifications color={'secondary'} />;
  const nativeInfo = useNative();

  if (!pushSupport) {
    return null;
  }

  const activatePush = async () => {
    try {
      await updatePushEntity(entity.entity, entity.id);
      if (!messagingDeviceToken) {
        try {
          const {getMessagingDeviceToken} = await import('app/services/firebaseMessaging');
          getMessagingDeviceToken(nativeInfo, dispatch, true, notificationPermission);
        } catch (error) {
          captureSentryException(error, {
            extra: {errorLocation: 'importing getMessagingDeviceToken in NotificationBanner'},
          });
        }
      } else {
        dispatch(updateSnackbarInfo(pushStatus('aktiviert')));
        dispatch(fetchFollows());
      }
      dispatch(askForEntityPushFinished());
    } catch (error) {
      captureSentryException(error, {extra: {errorLocation: 'activate Push via NotificationBanner'}});
    }
  };

  const cancelPush = () => {
    dispatch(askForEntityPushFinished());
    dispatch(updateSnackbarInfo(pushStatus('blockiert')));
  };

  const primaryAction = {
    label: 'Push aktivieren',
    action: activatePush,
  };

  const secondaryAction = {
    label: 'Jetzt nicht',
    action: cancelPush,
  };

  const text = `Du folgst jetzt ${entity?.name}. Möchtest du Push-Benachrichtigungen aktivieren?`;

  return (
    <Banner
      collapsedAppBar={collapsedAppBar}
      open={askForPush}
      text={text}
      icon={icon}
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
    />
  );
};

export {NotificationBanner};
